import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import mermaid from 'mermaid';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import './App.css';
import './Login.css';
import logo from './assets/logo.svg';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

mermaid.initialize({ startOnLoad: false, theme: 'dark' });

const Mermaid = ({ chart }) => {
  const [svg, setSvg] = useState('');

  useEffect(() => {
    async function renderDiagram() {
      try {
        // First, check if the syntax is valid without trying to render
        await mermaid.parse(chart);
        // If it's valid, then render it
        const { svg } = await mermaid.render('mermaid-diagram', chart);
        setSvg(svg);
      } catch (error) {
        setSvg(`<p style="color: #ff8a80;">Error: Invalid diagram syntax provided.</p>`);
      }
    }
    if (chart) {
      renderDiagram();
    }
  }, [chart]);

  return <div dangerouslySetInnerHTML={{ __html: svg }} />;
};

const CodeBlock = ({ file }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        await navigator.clipboard.writeText(file.content);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="code-block">
            <div className="code-header">
                <span>{file.path}</span>
                <button onClick={handleCopy}>{copied ? 'Copied!' : 'Copy'}</button>
            </div>
            <pre><code>{file.content}</code></pre>
        </div>
    );
};

function App() {
  const [prompt, setPrompt] = useState('');
  const [response, setResponse] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setResponse({ type: 'loading' });

    try {
      const headers = new Headers();
      headers.append('Authorization', 'Basic ' + btoa(username + ":" + password));
      headers.append('Content-Type', 'application/json');

      const res = await fetch(`${API_URL}/generate`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ prompt }),
      });

      if (res.status === 401) {
        setResponse({ type: 'error', message: 'Authentication failed.' });
        setIsLoggedIn(false);
        return;
      }

      const data = await res.json();
      setResponse(data);
    } catch (error) {
      setResponse({ type: 'error', message: 'An error occurred.' });
    }
  };
  
  const handleLogin = (e) => {
    e.preventDefault();
    // For simplicity, we just set isLoggedIn. The real check happens on the server.
    setIsLoggedIn(true);
  };
  
  if (!isLoggedIn) {
    return (
        <div className="login-container">
            <form className="login-form" onSubmit={handleLogin}>
                <img src={logo} className="login-logo" alt="N4L Logo" />
                <h1>N4L AI Copilot</h1>
                <input type="text" value={username} onChange={e => setUsername(e.target.value)} placeholder="Username" />
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" />
                <button type="submit">Login</button>
            </form>
        </div>
    );
  }

  const renderResponse = () => {
    if (!response) return null;

    switch (response.type) {
      case 'loading':
        return <p>Generating...</p>;
      case 'app':
        return (
          <div>
            <h3>Application Generated!</h3>
            {response.files.map(file => <CodeBlock key={file.path} file={file} />)}
          </div>
        );
      case 'campaign':
      case 'general':
        const content = response.plan || response.text;
        return (
            <div>
                <ReactMarkdown rehypePlugins={[rehypeRaw]}>{content}</ReactMarkdown>
                {response.diagram && <Mermaid chart={response.diagram} />}
            </div>
        );
      case 'funnel':
        return <Mermaid chart={response.diagram} />;
      case 'math':
        return <h2>Answer: {response.answer}</h2>;
      case 'error':
        return <p style={{ color: 'red' }}>Error: {response.message}</p>;
      default:
        return <pre>{JSON.stringify(response, null, 2)}</pre>;
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        {/* <img src={logo} className="logo" alt="N4L Logo" /> */}
        <h1>N4L AI Copilot</h1>
      </header>
      <main className="main-content">
        <form onSubmit={handleSubmit}>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Ask me to build an app, design a campaign, solve a math problem..."
          />
          <button type="submit">Generate</button>
        </form>
        {response && (
          <div className="response">
            {renderResponse()}
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
